import {
    createAsyncThunk,
    createSlice,
} from "@reduxjs/toolkit";

import API from "../../../utils/apiCalling";
import { config } from "../../../utils/apiUrl";

const api = new API();


/*
|--------------------------------------------------------------------------
| DEFAULT PAGINATION
|--------------------------------------------------------------------------
*/

const DEFAULT_PAGINATION = {
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 1,
    hasNextPage: false,
    hasPreviousPage: false,
};


/*
|--------------------------------------------------------------------------
| DEFAULT FILTERS
|--------------------------------------------------------------------------
*/

const DEFAULT_FILTERS = {
    fromDate: null,
    toDate: null,

    supplierId: null,

    status: "ALL",
    paymentStatus: "ALL",
    taxType: "ALL",

    financialYear: null,

    search: "",
};


/*
|--------------------------------------------------------------------------
| PURCHASE REGISTER SUMMARY
|--------------------------------------------------------------------------
*/

const DEFAULT_REGISTER_SUMMARY = {
    totalBills: 0,

    cancelledBills: 0,

    paidBills: 0,

    partiallyPaidBills: 0,

    unpaidBills: 0,

    totalQuantity: 0,

    grossAmount: 0,

    discountAmount: 0,

    taxableAmount: 0,

    cgstAmount: 0,

    sgstAmount: 0,

    igstAmount: 0,

    taxAmount: 0,

    cessAmount: 0,

    roundOffAmount: 0,

    grandTotal: 0,

    debitNoteAmount: 0,

    paidAmount: 0,

    outstandingAmount: 0,
};


/*
|--------------------------------------------------------------------------
| SUPPLIER SUMMARY
|--------------------------------------------------------------------------
*/

const DEFAULT_SUPPLIER_SUMMARY = {
    totalSuppliers: 0,

    totalBills: 0,

    grandTotal: 0,

    taxAmount: 0,

    debitNoteAmount: 0,

    paidAmount: 0,

    outstandingAmount: 0,
};


/*
|--------------------------------------------------------------------------
| DEBIT NOTE SUMMARY
|--------------------------------------------------------------------------
*/

const DEFAULT_DEBIT_NOTE_SUMMARY = {
    totalDebitNotes: 0,

    draft: 0,

    confirmed: 0,

    cancelled: 0,

    totalDebitAmount: 0,

    confirmedDebitAmount: 0,

    taxableAmount: 0,

    taxAmount: 0,
};


/*
|--------------------------------------------------------------------------
| PAYMENT OUT SUMMARY
|--------------------------------------------------------------------------
*/

const DEFAULT_PAYMENT_OUT_SUMMARY = {
    totalPayments: 0,

    reversedPayments: 0,

    totalPaid: 0,

    allocatedAmount: 0,

    advanceAmount: 0,

    reversedAmount: 0,
};


/*
|--------------------------------------------------------------------------
| OUTSTANDING SUMMARY
|--------------------------------------------------------------------------
*/

const DEFAULT_OUTSTANDING_SUMMARY = {
    totalBills: 0,

    overdueBills: 0,

    outstandingAmount: 0,

    overdueAmount: 0,

    notDueAmount: 0,
};


/*
|--------------------------------------------------------------------------
| AGEING SUMMARY
|--------------------------------------------------------------------------
*/

const DEFAULT_AGEING_SUMMARY = {
    totalSuppliers: 0,

    notDue: 0,

    days0To30: 0,

    days31To60: 0,

    days61To90: 0,

    days90Plus: 0,

    totalOutstanding: 0,
};


/*
|--------------------------------------------------------------------------
| GST INPUT SUMMARY
|--------------------------------------------------------------------------
*/

const DEFAULT_GST_SUMMARY = {
    totalBills: 0,

    taxableAmount: 0,

    cgstAmount: 0,

    sgstAmount: 0,

    igstAmount: 0,

    cessAmount: 0,

    inputTax: 0,

    grandTotal: 0,
};


/*
|--------------------------------------------------------------------------
| ERROR HELPER
|--------------------------------------------------------------------------
*/

const getErrorMessage = (
    error,
    fallback = "Something went wrong."
) => {
    if (
        typeof error === "string"
    ) {
        return error;
    }

    return (
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.data?.message ||
        error?.message ||
        fallback
    );
};


/*
|--------------------------------------------------------------------------
| RESPONSE HELPERS
|--------------------------------------------------------------------------
*/

const getResponseEnvelope = (
    response
) => {
    if (
        !response
    ) {
        return {};
    }

    /*
    |--------------------------------------------------------------------------
    | Axios response
    |--------------------------------------------------------------------------
    */

    if (
        response?.data &&
        typeof response.data === "object" &&
        !Array.isArray(
            response.data
        ) &&
        (
            response?.status !== undefined ||
            response?.headers !== undefined ||
            response?.config !== undefined
        )
    ) {
        return response.data;
    }

    /*
    |--------------------------------------------------------------------------
    | Custom API may already return response body
    |--------------------------------------------------------------------------
    */

    return response;
};


const getResponseData = (
    response
) => {
    const envelope =
        getResponseEnvelope(
            response
        );

    return (
        envelope?.data ??
        envelope
    );
};


/*
|--------------------------------------------------------------------------
| QUERY STRING
|--------------------------------------------------------------------------
*/

const buildQueryString = (
    params = {}
) => {
    const query =
        new URLSearchParams();

    Object.entries(
        params
    ).forEach(
        ([
            key,
            value,
        ]) => {
            if (
                value === undefined ||
                value === null ||
                value === ""
            ) {
                return;
            }

            /*
            |--------------------------------------------------------------------------
            | Don't send ALL filters
            |--------------------------------------------------------------------------
            */

            if (
                [
                    "status",
                    "paymentStatus",
                    "taxType",
                    "paymentMethod",
                ].includes(
                    key
                ) &&
                String(
                    value
                ).toUpperCase() === "ALL"
            ) {
                return;
            }

            query.append(
                key,
                value
            );
        }
    );

    const queryString =
        query.toString();

    return queryString
        ? `?${queryString}`
        : "";
};


/*
|--------------------------------------------------------------------------
| NORMALIZE REPORT RESPONSE
|--------------------------------------------------------------------------
*/

const normalizeReportResponse = (
    response,
    defaultSummary
) => {
    const data =
        getResponseData(
            response
        ) || {};

    return {
        records:
            Array.isArray(
                data?.records
            )
                ? data.records
                : [],

        summary: {
            ...defaultSummary,

            ...(data?.summary ||
                {}),
        },

        pagination: {
            ...DEFAULT_PAGINATION,

            ...(data?.pagination ||
                {}),
        },

        filters:
            data?.filters ||
            {},
    };
};


/*
|--------------------------------------------------------------------------
| PURCHASE REGISTER
|--------------------------------------------------------------------------
*/

export const fetchPurchaseRegister =
    createAsyncThunk(
        "purchaseReport/fetchPurchaseRegister",

        async (
            params = {},
            {
                rejectWithValue,
            }
        ) => {
            try {
                const queryString =
                    buildQueryString({
                        page:
                            params.page,

                        limit:
                            params.limit,

                        fromDate:
                            params.fromDate,

                        toDate:
                            params.toDate,

                        supplierId:
                            params.supplierId,

                        status:
                            params.status,

                        paymentStatus:
                            params.paymentStatus,

                        taxType:
                            params.taxType,

                        financialYear:
                            params.financialYear,

                        search:
                            params.search,

                        sortBy:
                            params.sortBy,

                        sortOrder:
                            params.sortOrder,
                    });

                const response =
                    await api.get(
                        `${config.getPurchaseRegister()}${queryString}`
                    );

                return normalizeReportResponse(
                    response,
                    DEFAULT_REGISTER_SUMMARY
                );
            } catch (
                error
            ) {
                return rejectWithValue(
                    getErrorMessage(
                        error,
                        "Unable to load purchase register."
                    )
                );
            }
        }
    );


/*
|--------------------------------------------------------------------------
| PURCHASE REGISTER SUMMARY
|--------------------------------------------------------------------------
*/

export const fetchPurchaseRegisterSummary =
    createAsyncThunk(
        "purchaseReport/fetchPurchaseRegisterSummary",

        async (
            params = {},
            {
                rejectWithValue,
            }
        ) => {
            try {
                const queryString =
                    buildQueryString({
                        fromDate:
                            params.fromDate,

                        toDate:
                            params.toDate,

                        supplierId:
                            params.supplierId,

                        status:
                            params.status,

                        paymentStatus:
                            params.paymentStatus,

                        taxType:
                            params.taxType,

                        financialYear:
                            params.financialYear,

                        search:
                            params.search,
                    });

                const response =
                    await api.get(
                        `${config.getPurchaseRegisterSummary()}${queryString}`
                    );

                return {
                    ...DEFAULT_REGISTER_SUMMARY,

                    ...(getResponseData(
                        response
                    ) || {}),
                };
            } catch (
                error
            ) {
                return rejectWithValue(
                    getErrorMessage(
                        error,
                        "Unable to load purchase register summary."
                    )
                );
            }
        }
    );


/*
|--------------------------------------------------------------------------
| SUPPLIER PURCHASE SUMMARY
|--------------------------------------------------------------------------
*/

export const fetchSupplierPurchaseSummary =
    createAsyncThunk(
        "purchaseReport/fetchSupplierPurchaseSummary",

        async (
            params = {},
            {
                rejectWithValue,
            }
        ) => {
            try {
                const queryString =
                    buildQueryString({
                        page:
                            params.page,

                        limit:
                            params.limit,

                        fromDate:
                            params.fromDate,

                        toDate:
                            params.toDate,

                        supplierId:
                            params.supplierId,

                        status:
                            params.status,

                        paymentStatus:
                            params.paymentStatus,

                        taxType:
                            params.taxType,

                        financialYear:
                            params.financialYear,

                        search:
                            params.search,

                        sortBy:
                            params.sortBy,

                        sortOrder:
                            params.sortOrder,
                    });

                const response =
                    await api.get(
                        `${config.getSupplierPurchaseSummary()}${queryString}`
                    );

                return normalizeReportResponse(
                    response,
                    DEFAULT_SUPPLIER_SUMMARY
                );
            } catch (
                error
            ) {
                return rejectWithValue(
                    getErrorMessage(
                        error,
                        "Unable to load supplier purchase summary."
                    )
                );
            }
        }
    );


/*
|--------------------------------------------------------------------------
| DEBIT NOTE REPORT
|--------------------------------------------------------------------------
*/

export const fetchPurchaseDebitNoteReport =
    createAsyncThunk(
        "purchaseReport/fetchPurchaseDebitNoteReport",

        async (
            params = {},
            {
                rejectWithValue,
            }
        ) => {
            try {
                const queryString =
                    buildQueryString({
                        page:
                            params.page,

                        limit:
                            params.limit,

                        fromDate:
                            params.fromDate,

                        toDate:
                            params.toDate,

                        supplierId:
                            params.supplierId,

                        purchaseBillId:
                            params.purchaseBillId,

                        status:
                            params.status,

                        search:
                            params.search,

                        sortBy:
                            params.sortBy,

                        sortOrder:
                            params.sortOrder,
                    });

                const response =
                    await api.get(
                        `${config.getPurchaseDebitNoteReport()}${queryString}`
                    );

                return normalizeReportResponse(
                    response,
                    DEFAULT_DEBIT_NOTE_SUMMARY
                );
            } catch (
                error
            ) {
                return rejectWithValue(
                    getErrorMessage(
                        error,
                        "Unable to load debit note report."
                    )
                );
            }
        }
    );


/*
|--------------------------------------------------------------------------
| PAYMENT OUT REPORT
|--------------------------------------------------------------------------
*/

export const fetchPurchasePaymentOutReport =
    createAsyncThunk(
        "purchaseReport/fetchPurchasePaymentOutReport",

        async (
            params = {},
            {
                rejectWithValue,
            }
        ) => {
            try {
                const queryString =
                    buildQueryString({
                        page:
                            params.page,

                        limit:
                            params.limit,

                        fromDate:
                            params.fromDate,

                        toDate:
                            params.toDate,

                        supplierId:
                            params.supplierId,

                        status:
                            params.status,

                        paymentMethod:
                            params.paymentMethod,

                        search:
                            params.search,

                        sortBy:
                            params.sortBy,

                        sortOrder:
                            params.sortOrder,
                    });

                const response =
                    await api.get(
                        `${config.getPurchasePaymentOutReport()}${queryString}`
                    );

                return normalizeReportResponse(
                    response,
                    DEFAULT_PAYMENT_OUT_SUMMARY
                );
            } catch (
                error
            ) {
                return rejectWithValue(
                    getErrorMessage(
                        error,
                        "Unable to load payment out report."
                    )
                );
            }
        }
    );


/*
|--------------------------------------------------------------------------
| OUTSTANDING REPORT
|--------------------------------------------------------------------------
*/

export const fetchPurchaseOutstandingReport =
    createAsyncThunk(
        "purchaseReport/fetchPurchaseOutstandingReport",

        async (
            params = {},
            {
                rejectWithValue,
            }
        ) => {
            try {
                const queryString =
                    buildQueryString({
                        page:
                            params.page,

                        limit:
                            params.limit,

                        fromDate:
                            params.fromDate,

                        toDate:
                            params.toDate,

                        supplierId:
                            params.supplierId,

                        status:
                            params.status,

                        taxType:
                            params.taxType,

                        financialYear:
                            params.financialYear,

                        search:
                            params.search,

                        sortBy:
                            params.sortBy,

                        sortOrder:
                            params.sortOrder,
                    });

                const response =
                    await api.get(
                        `${config.getPurchaseOutstandingReport()}${queryString}`
                    );

                return normalizeReportResponse(
                    response,
                    DEFAULT_OUTSTANDING_SUMMARY
                );
            } catch (
                error
            ) {
                return rejectWithValue(
                    getErrorMessage(
                        error,
                        "Unable to load outstanding report."
                    )
                );
            }
        }
    );


/*
|--------------------------------------------------------------------------
| AGEING REPORT
|--------------------------------------------------------------------------
*/

export const fetchPurchaseAgeingReport =
    createAsyncThunk(
        "purchaseReport/fetchPurchaseAgeingReport",

        async (
            params = {},
            {
                rejectWithValue,
            }
        ) => {
            try {
                const queryString =
                    buildQueryString({
                        page:
                            params.page,

                        limit:
                            params.limit,

                        fromDate:
                            params.fromDate,

                        toDate:
                            params.toDate,

                        supplierId:
                            params.supplierId,

                        status:
                            params.status,

                        financialYear:
                            params.financialYear,

                        search:
                            params.search,

                        sortBy:
                            params.sortBy,

                        sortOrder:
                            params.sortOrder,
                    });

                const response =
                    await api.get(
                        `${config.getPurchaseAgeingReport()}${queryString}`
                    );

                return normalizeReportResponse(
                    response,
                    DEFAULT_AGEING_SUMMARY
                );
            } catch (
                error
            ) {
                return rejectWithValue(
                    getErrorMessage(
                        error,
                        "Unable to load ageing report."
                    )
                );
            }
        }
    );


/*
|--------------------------------------------------------------------------
| GST INPUT REPORT
|--------------------------------------------------------------------------
*/

export const fetchPurchaseGstInputReport =
    createAsyncThunk(
        "purchaseReport/fetchPurchaseGstInputReport",

        async (
            params = {},
            {
                rejectWithValue,
            }
        ) => {
            try {
                const queryString =
                    buildQueryString({
                        page:
                            params.page,

                        limit:
                            params.limit,

                        fromDate:
                            params.fromDate,

                        toDate:
                            params.toDate,

                        supplierId:
                            params.supplierId,

                        status:
                            params.status,

                        taxType:
                            params.taxType,

                        financialYear:
                            params.financialYear,

                        search:
                            params.search,

                        sortBy:
                            params.sortBy,

                        sortOrder:
                            params.sortOrder,
                    });

                const response =
                    await api.get(
                        `${config.getPurchaseGstInputReport()}${queryString}`
                    );

                return normalizeReportResponse(
                    response,
                    DEFAULT_GST_SUMMARY
                );
            } catch (
                error
            ) {
                return rejectWithValue(
                    getErrorMessage(
                        error,
                        "Unable to load GST input report."
                    )
                );
            }
        }
    );


/*
|--------------------------------------------------------------------------
| INITIAL STATE
|--------------------------------------------------------------------------
*/

const initialState = {
    /*
    |--------------------------------------------------------------------------
    | PURCHASE REGISTER
    |--------------------------------------------------------------------------
    */

    purchaseRegister: {
        records: [],

        summary: {
            ...DEFAULT_REGISTER_SUMMARY,
        },

        pagination: {
            ...DEFAULT_PAGINATION,
        },

        filters: {
            ...DEFAULT_FILTERS,
        },
    },


    /*
    |--------------------------------------------------------------------------
    | REGISTER SUMMARY
    |--------------------------------------------------------------------------
    */

    registerSummary: {
        ...DEFAULT_REGISTER_SUMMARY,
    },


    /*
    |--------------------------------------------------------------------------
    | SUPPLIER SUMMARY
    |--------------------------------------------------------------------------
    */

    supplierSummary: {
        records: [],

        summary: {
            ...DEFAULT_SUPPLIER_SUMMARY,
        },

        pagination: {
            ...DEFAULT_PAGINATION,
        },
    },


    /*
    |--------------------------------------------------------------------------
    | DEBIT NOTE REPORT
    |--------------------------------------------------------------------------
    */

    debitNoteReport: {
        records: [],

        summary: {
            ...DEFAULT_DEBIT_NOTE_SUMMARY,
        },

        pagination: {
            ...DEFAULT_PAGINATION,
        },
    },


    /*
    |--------------------------------------------------------------------------
    | PAYMENT OUT REPORT
    |--------------------------------------------------------------------------
    */

    paymentOutReport: {
        records: [],

        summary: {
            ...DEFAULT_PAYMENT_OUT_SUMMARY,
        },

        pagination: {
            ...DEFAULT_PAGINATION,
        },
    },


    /*
    |--------------------------------------------------------------------------
    | OUTSTANDING REPORT
    |--------------------------------------------------------------------------
    */

    outstandingReport: {
        records: [],

        summary: {
            ...DEFAULT_OUTSTANDING_SUMMARY,
        },

        pagination: {
            ...DEFAULT_PAGINATION,
        },
    },


    /*
    |--------------------------------------------------------------------------
    | AGEING REPORT
    |--------------------------------------------------------------------------
    */

    ageingReport: {
        records: [],

        summary: {
            ...DEFAULT_AGEING_SUMMARY,
        },

        pagination: {
            ...DEFAULT_PAGINATION,
        },
    },


    /*
    |--------------------------------------------------------------------------
    | GST INPUT REPORT
    |--------------------------------------------------------------------------
    */

    gstInputReport: {
        records: [],

        summary: {
            ...DEFAULT_GST_SUMMARY,
        },

        pagination: {
            ...DEFAULT_PAGINATION,
        },
    },


    /*
    |--------------------------------------------------------------------------
    | LOADING
    |--------------------------------------------------------------------------
    */

    loading: {
        register: false,

        registerSummary: false,

        supplierSummary: false,

        debitNotes: false,

        paymentOut: false,

        outstanding: false,

        ageing: false,

        gstInput: false,
    },


    /*
    |--------------------------------------------------------------------------
    | ERROR
    |--------------------------------------------------------------------------
    */

    error: {
        register: null,

        registerSummary: null,

        supplierSummary: null,

        debitNotes: null,

        paymentOut: null,

        outstanding: null,

        ageing: null,

        gstInput: null,
    },
};


/*
|--------------------------------------------------------------------------
| COMMON STATE SETTER
|--------------------------------------------------------------------------
*/

const setReportState = (
    state,
    key,
    payload,
    defaultSummary
) => {
    state[key].records =
        Array.isArray(
            payload?.records
        )
            ? payload.records
            : [];

    state[key].summary = {
        ...defaultSummary,

        ...(payload?.summary ||
            {}),
    };

    state[key].pagination = {
        ...DEFAULT_PAGINATION,

        ...(payload?.pagination ||
            {}),
    };
};


/*
|--------------------------------------------------------------------------
| SLICE
|--------------------------------------------------------------------------
*/

const purchaseReportSlice =
    createSlice({
        name:
            "purchaseReport",

        initialState,

        reducers: {
            /*
            |--------------------------------------------------------------------------
            | CLEAR PURCHASE REGISTER
            |--------------------------------------------------------------------------
            */

            clearPurchaseRegister: (
                state
            ) => {
                state.purchaseRegister = {
                    records: [],

                    summary: {
                        ...DEFAULT_REGISTER_SUMMARY,
                    },

                    pagination: {
                        ...DEFAULT_PAGINATION,
                    },

                    filters: {
                        ...DEFAULT_FILTERS,
                    },
                };

                state.error.register =
                    null;
            },


            /*
            |--------------------------------------------------------------------------
            | CLEAR REGISTER SUMMARY
            |--------------------------------------------------------------------------
            */

            clearPurchaseRegisterSummary: (
                state
            ) => {
                state.registerSummary = {
                    ...DEFAULT_REGISTER_SUMMARY,
                };

                state.error.registerSummary =
                    null;
            },


            /*
            |--------------------------------------------------------------------------
            | CLEAR SUPPLIER SUMMARY
            |--------------------------------------------------------------------------
            */

            clearSupplierPurchaseSummary: (
                state
            ) => {
                state.supplierSummary = {
                    records: [],

                    summary: {
                        ...DEFAULT_SUPPLIER_SUMMARY,
                    },

                    pagination: {
                        ...DEFAULT_PAGINATION,
                    },
                };

                state.error.supplierSummary =
                    null;
            },


            /*
            |--------------------------------------------------------------------------
            | CLEAR DEBIT NOTE REPORT
            |--------------------------------------------------------------------------
            */

            clearPurchaseDebitNoteReport: (
                state
            ) => {
                state.debitNoteReport = {
                    records: [],

                    summary: {
                        ...DEFAULT_DEBIT_NOTE_SUMMARY,
                    },

                    pagination: {
                        ...DEFAULT_PAGINATION,
                    },
                };

                state.error.debitNotes =
                    null;
            },


            /*
            |--------------------------------------------------------------------------
            | CLEAR PAYMENT OUT REPORT
            |--------------------------------------------------------------------------
            */

            clearPurchasePaymentOutReport: (
                state
            ) => {
                state.paymentOutReport = {
                    records: [],

                    summary: {
                        ...DEFAULT_PAYMENT_OUT_SUMMARY,
                    },

                    pagination: {
                        ...DEFAULT_PAGINATION,
                    },
                };

                state.error.paymentOut =
                    null;
            },


            /*
            |--------------------------------------------------------------------------
            | CLEAR OUTSTANDING REPORT
            |--------------------------------------------------------------------------
            */

            clearPurchaseOutstandingReport: (
                state
            ) => {
                state.outstandingReport = {
                    records: [],

                    summary: {
                        ...DEFAULT_OUTSTANDING_SUMMARY,
                    },

                    pagination: {
                        ...DEFAULT_PAGINATION,
                    },
                };

                state.error.outstanding =
                    null;
            },


            /*
            |--------------------------------------------------------------------------
            | CLEAR AGEING REPORT
            |--------------------------------------------------------------------------
            */

            clearPurchaseAgeingReport: (
                state
            ) => {
                state.ageingReport = {
                    records: [],

                    summary: {
                        ...DEFAULT_AGEING_SUMMARY,
                    },

                    pagination: {
                        ...DEFAULT_PAGINATION,
                    },
                };

                state.error.ageing =
                    null;
            },


            /*
            |--------------------------------------------------------------------------
            | CLEAR GST INPUT REPORT
            |--------------------------------------------------------------------------
            */

            clearPurchaseGstInputReport: (
                state
            ) => {
                state.gstInputReport = {
                    records: [],

                    summary: {
                        ...DEFAULT_GST_SUMMARY,
                    },

                    pagination: {
                        ...DEFAULT_PAGINATION,
                    },
                };

                state.error.gstInput =
                    null;
            },


            /*
            |--------------------------------------------------------------------------
            | SET REGISTER FILTERS
            |--------------------------------------------------------------------------
            */

            setPurchaseRegisterFilters: (
                state,
                action
            ) => {
                state.purchaseRegister.filters = {
                    ...state
                        .purchaseRegister
                        .filters,

                    ...(action.payload ||
                        {}),
                };
            },


            /*
            |--------------------------------------------------------------------------
            | RESET REGISTER FILTERS
            |--------------------------------------------------------------------------
            */

            resetPurchaseRegisterFilters: (
                state
            ) => {
                state.purchaseRegister.filters = {
                    ...DEFAULT_FILTERS,
                };
            },


            /*
            |--------------------------------------------------------------------------
            | CLEAR ALL ERRORS
            |--------------------------------------------------------------------------
            */

            clearPurchaseReportErrors: (
                state
            ) => {
                state.error = {
                    register: null,

                    registerSummary: null,

                    supplierSummary: null,

                    debitNotes: null,

                    paymentOut: null,

                    outstanding: null,

                    ageing: null,

                    gstInput: null,
                };
            },


            /*
            |--------------------------------------------------------------------------
            | RESET FULL STATE
            |--------------------------------------------------------------------------
            */

            resetPurchaseReportState:
                () => ({
                    purchaseRegister: {
                        records: [],

                        summary: {
                            ...DEFAULT_REGISTER_SUMMARY,
                        },

                        pagination: {
                            ...DEFAULT_PAGINATION,
                        },

                        filters: {
                            ...DEFAULT_FILTERS,
                        },
                    },

                    registerSummary: {
                        ...DEFAULT_REGISTER_SUMMARY,
                    },

                    supplierSummary: {
                        records: [],

                        summary: {
                            ...DEFAULT_SUPPLIER_SUMMARY,
                        },

                        pagination: {
                            ...DEFAULT_PAGINATION,
                        },
                    },

                    debitNoteReport: {
                        records: [],

                        summary: {
                            ...DEFAULT_DEBIT_NOTE_SUMMARY,
                        },

                        pagination: {
                            ...DEFAULT_PAGINATION,
                        },
                    },

                    paymentOutReport: {
                        records: [],

                        summary: {
                            ...DEFAULT_PAYMENT_OUT_SUMMARY,
                        },

                        pagination: {
                            ...DEFAULT_PAGINATION,
                        },
                    },

                    outstandingReport: {
                        records: [],

                        summary: {
                            ...DEFAULT_OUTSTANDING_SUMMARY,
                        },

                        pagination: {
                            ...DEFAULT_PAGINATION,
                        },
                    },

                    ageingReport: {
                        records: [],

                        summary: {
                            ...DEFAULT_AGEING_SUMMARY,
                        },

                        pagination: {
                            ...DEFAULT_PAGINATION,
                        },
                    },

                    gstInputReport: {
                        records: [],

                        summary: {
                            ...DEFAULT_GST_SUMMARY,
                        },

                        pagination: {
                            ...DEFAULT_PAGINATION,
                        },
                    },

                    loading: {
                        register: false,

                        registerSummary: false,

                        supplierSummary: false,

                        debitNotes: false,

                        paymentOut: false,

                        outstanding: false,

                        ageing: false,

                        gstInput: false,
                    },

                    error: {
                        register: null,

                        registerSummary: null,

                        supplierSummary: null,

                        debitNotes: null,

                        paymentOut: null,

                        outstanding: null,

                        ageing: null,

                        gstInput: null,
                    },
                }),
        },


        /*
        |--------------------------------------------------------------------------
        | EXTRA REDUCERS
        |--------------------------------------------------------------------------
        */

        extraReducers: (
            builder
        ) => {
            /*
            |--------------------------------------------------------------------------
            | PURCHASE REGISTER
            |--------------------------------------------------------------------------
            */

            builder
                .addCase(
                    fetchPurchaseRegister
                        .pending,

                    (
                        state
                    ) => {
                        state.loading.register =
                            true;

                        state.error.register =
                            null;
                    }
                )

                .addCase(
                    fetchPurchaseRegister
                        .fulfilled,

                    (
                        state,
                        action
                    ) => {
                        state.loading.register =
                            false;

                        setReportState(
                            state,
                            "purchaseRegister",
                            action.payload,
                            DEFAULT_REGISTER_SUMMARY
                        );

                        state.purchaseRegister.filters = {
                            ...DEFAULT_FILTERS,

                            ...(action
                                .payload
                                ?.filters ||
                                {}),
                        };
                    }
                )

                .addCase(
                    fetchPurchaseRegister
                        .rejected,

                    (
                        state,
                        action
                    ) => {
                        state.loading.register =
                            false;

                        state.error.register =
                            action.payload ||
                            action.error
                                ?.message ||
                            "Unable to load purchase register.";
                    }
                );


            /*
            |--------------------------------------------------------------------------
            | REGISTER SUMMARY
            |--------------------------------------------------------------------------
            */

            builder
                .addCase(
                    fetchPurchaseRegisterSummary
                        .pending,

                    (
                        state
                    ) => {
                        state.loading.registerSummary =
                            true;

                        state.error.registerSummary =
                            null;
                    }
                )

                .addCase(
                    fetchPurchaseRegisterSummary
                        .fulfilled,

                    (
                        state,
                        action
                    ) => {
                        state.loading.registerSummary =
                            false;

                        state.registerSummary = {
                            ...DEFAULT_REGISTER_SUMMARY,

                            ...(action.payload ||
                                {}),
                        };
                    }
                )

                .addCase(
                    fetchPurchaseRegisterSummary
                        .rejected,

                    (
                        state,
                        action
                    ) => {
                        state.loading.registerSummary =
                            false;

                        state.error.registerSummary =
                            action.payload ||
                            action.error
                                ?.message ||
                            "Unable to load purchase register summary.";
                    }
                );


            /*
            |--------------------------------------------------------------------------
            | SUPPLIER SUMMARY
            |--------------------------------------------------------------------------
            */

            builder
                .addCase(
                    fetchSupplierPurchaseSummary
                        .pending,

                    (
                        state
                    ) => {
                        state.loading.supplierSummary =
                            true;

                        state.error.supplierSummary =
                            null;
                    }
                )

                .addCase(
                    fetchSupplierPurchaseSummary
                        .fulfilled,

                    (
                        state,
                        action
                    ) => {
                        state.loading.supplierSummary =
                            false;

                        setReportState(
                            state,
                            "supplierSummary",
                            action.payload,
                            DEFAULT_SUPPLIER_SUMMARY
                        );
                    }
                )

                .addCase(
                    fetchSupplierPurchaseSummary
                        .rejected,

                    (
                        state,
                        action
                    ) => {
                        state.loading.supplierSummary =
                            false;

                        state.error.supplierSummary =
                            action.payload ||
                            action.error
                                ?.message ||
                            "Unable to load supplier summary.";
                    }
                );


            /*
            |--------------------------------------------------------------------------
            | DEBIT NOTE
            |--------------------------------------------------------------------------
            */

            builder
                .addCase(
                    fetchPurchaseDebitNoteReport
                        .pending,

                    (
                        state
                    ) => {
                        state.loading.debitNotes =
                            true;

                        state.error.debitNotes =
                            null;
                    }
                )

                .addCase(
                    fetchPurchaseDebitNoteReport
                        .fulfilled,

                    (
                        state,
                        action
                    ) => {
                        state.loading.debitNotes =
                            false;

                        setReportState(
                            state,
                            "debitNoteReport",
                            action.payload,
                            DEFAULT_DEBIT_NOTE_SUMMARY
                        );
                    }
                )

                .addCase(
                    fetchPurchaseDebitNoteReport
                        .rejected,

                    (
                        state,
                        action
                    ) => {
                        state.loading.debitNotes =
                            false;

                        state.error.debitNotes =
                            action.payload ||
                            action.error
                                ?.message ||
                            "Unable to load debit note report.";
                    }
                );


            /*
            |--------------------------------------------------------------------------
            | PAYMENT OUT
            |--------------------------------------------------------------------------
            */

            builder
                .addCase(
                    fetchPurchasePaymentOutReport
                        .pending,

                    (
                        state
                    ) => {
                        state.loading.paymentOut =
                            true;

                        state.error.paymentOut =
                            null;
                    }
                )

                .addCase(
                    fetchPurchasePaymentOutReport
                        .fulfilled,

                    (
                        state,
                        action
                    ) => {
                        state.loading.paymentOut =
                            false;

                        setReportState(
                            state,
                            "paymentOutReport",
                            action.payload,
                            DEFAULT_PAYMENT_OUT_SUMMARY
                        );
                    }
                )

                .addCase(
                    fetchPurchasePaymentOutReport
                        .rejected,

                    (
                        state,
                        action
                    ) => {
                        state.loading.paymentOut =
                            false;

                        state.error.paymentOut =
                            action.payload ||
                            action.error
                                ?.message ||
                            "Unable to load payment out report.";
                    }
                );


            /*
            |--------------------------------------------------------------------------
            | OUTSTANDING
            |--------------------------------------------------------------------------
            */

            builder
                .addCase(
                    fetchPurchaseOutstandingReport
                        .pending,

                    (
                        state
                    ) => {
                        state.loading.outstanding =
                            true;

                        state.error.outstanding =
                            null;
                    }
                )

                .addCase(
                    fetchPurchaseOutstandingReport
                        .fulfilled,

                    (
                        state,
                        action
                    ) => {
                        state.loading.outstanding =
                            false;

                        setReportState(
                            state,
                            "outstandingReport",
                            action.payload,
                            DEFAULT_OUTSTANDING_SUMMARY
                        );
                    }
                )

                .addCase(
                    fetchPurchaseOutstandingReport
                        .rejected,

                    (
                        state,
                        action
                    ) => {
                        state.loading.outstanding =
                            false;

                        state.error.outstanding =
                            action.payload ||
                            action.error
                                ?.message ||
                            "Unable to load outstanding report.";
                    }
                );


            /*
            |--------------------------------------------------------------------------
            | AGEING
            |--------------------------------------------------------------------------
            */

            builder
                .addCase(
                    fetchPurchaseAgeingReport
                        .pending,

                    (
                        state
                    ) => {
                        state.loading.ageing =
                            true;

                        state.error.ageing =
                            null;
                    }
                )

                .addCase(
                    fetchPurchaseAgeingReport
                        .fulfilled,

                    (
                        state,
                        action
                    ) => {
                        state.loading.ageing =
                            false;

                        setReportState(
                            state,
                            "ageingReport",
                            action.payload,
                            DEFAULT_AGEING_SUMMARY
                        );
                    }
                )

                .addCase(
                    fetchPurchaseAgeingReport
                        .rejected,

                    (
                        state,
                        action
                    ) => {
                        state.loading.ageing =
                            false;

                        state.error.ageing =
                            action.payload ||
                            action.error
                                ?.message ||
                            "Unable to load ageing report.";
                    }
                );


            /*
            |--------------------------------------------------------------------------
            | GST INPUT
            |--------------------------------------------------------------------------
            */

            builder
                .addCase(
                    fetchPurchaseGstInputReport
                        .pending,

                    (
                        state
                    ) => {
                        state.loading.gstInput =
                            true;

                        state.error.gstInput =
                            null;
                    }
                )

                .addCase(
                    fetchPurchaseGstInputReport
                        .fulfilled,

                    (
                        state,
                        action
                    ) => {
                        state.loading.gstInput =
                            false;

                        setReportState(
                            state,
                            "gstInputReport",
                            action.payload,
                            DEFAULT_GST_SUMMARY
                        );
                    }
                )

                .addCase(
                    fetchPurchaseGstInputReport
                        .rejected,

                    (
                        state,
                        action
                    ) => {
                        state.loading.gstInput =
                            false;

                        state.error.gstInput =
                            action.payload ||
                            action.error
                                ?.message ||
                            "Unable to load GST input report.";
                    }
                );
        },
    });


/*
|--------------------------------------------------------------------------
| ACTIONS
|--------------------------------------------------------------------------
*/

export const {
    clearPurchaseRegister,

    clearPurchaseRegisterSummary,

    clearSupplierPurchaseSummary,

    clearPurchaseDebitNoteReport,

    clearPurchasePaymentOutReport,

    clearPurchaseOutstandingReport,

    clearPurchaseAgeingReport,

    clearPurchaseGstInputReport,

    setPurchaseRegisterFilters,

    resetPurchaseRegisterFilters,

    clearPurchaseReportErrors,

    resetPurchaseReportState,
} =
    purchaseReportSlice.actions;


/*
|--------------------------------------------------------------------------
| ROOT
|--------------------------------------------------------------------------
*/

export const purchaseReportStateSelector = (
    state
) =>
    state?.purchaseReport ||
    initialState;


/*
|--------------------------------------------------------------------------
| PURCHASE REGISTER SELECTORS
|--------------------------------------------------------------------------
*/

export const purchaseRegisterSelector = (
    state
) =>
    state?.purchaseReport
        ?.purchaseRegister ||
    initialState.purchaseRegister;


export const purchaseRegisterRecordsSelector = (
    state
) =>
    state?.purchaseReport
        ?.purchaseRegister
        ?.records ||
    [];


export const purchaseRegisterSummarySelector = (
    state
) =>
    state?.purchaseReport
        ?.purchaseRegister
        ?.summary ||
    DEFAULT_REGISTER_SUMMARY;


export const purchaseRegisterPaginationSelector = (
    state
) =>
    state?.purchaseReport
        ?.purchaseRegister
        ?.pagination ||
    DEFAULT_PAGINATION;


export const purchaseRegisterFiltersSelector = (
    state
) =>
    state?.purchaseReport
        ?.purchaseRegister
        ?.filters ||
    DEFAULT_FILTERS;


/*
|--------------------------------------------------------------------------
| REGISTER SUMMARY SELECTOR
|--------------------------------------------------------------------------
*/

export const purchaseReportSummarySelector = (
    state
) =>
    state?.purchaseReport
        ?.registerSummary ||
    DEFAULT_REGISTER_SUMMARY;


/*
|--------------------------------------------------------------------------
| SUPPLIER SUMMARY SELECTORS
|--------------------------------------------------------------------------
*/

export const supplierPurchaseSummarySelector = (
    state
) =>
    state?.purchaseReport
        ?.supplierSummary ||
    initialState.supplierSummary;


export const supplierPurchaseSummaryRecordsSelector = (
    state
) =>
    state?.purchaseReport
        ?.supplierSummary
        ?.records ||
    [];


export const supplierPurchaseSummaryTotalsSelector = (
    state
) =>
    state?.purchaseReport
        ?.supplierSummary
        ?.summary ||
    DEFAULT_SUPPLIER_SUMMARY;


export const supplierPurchaseSummaryPaginationSelector = (
    state
) =>
    state?.purchaseReport
        ?.supplierSummary
        ?.pagination ||
    DEFAULT_PAGINATION;


/*
|--------------------------------------------------------------------------
| DEBIT NOTE SELECTORS
|--------------------------------------------------------------------------
*/

export const purchaseDebitNoteReportSelector = (
    state
) =>
    state?.purchaseReport
        ?.debitNoteReport ||
    initialState.debitNoteReport;


export const purchaseDebitNoteRecordsSelector = (
    state
) =>
    state?.purchaseReport
        ?.debitNoteReport
        ?.records ||
    [];


export const purchaseDebitNoteSummarySelector = (
    state
) =>
    state?.purchaseReport
        ?.debitNoteReport
        ?.summary ||
    DEFAULT_DEBIT_NOTE_SUMMARY;


export const purchaseDebitNotePaginationSelector = (
    state
) =>
    state?.purchaseReport
        ?.debitNoteReport
        ?.pagination ||
    DEFAULT_PAGINATION;


/*
|--------------------------------------------------------------------------
| PAYMENT OUT SELECTORS
|--------------------------------------------------------------------------
*/

export const purchasePaymentOutReportSelector = (
    state
) =>
    state?.purchaseReport
        ?.paymentOutReport ||
    initialState.paymentOutReport;


export const purchasePaymentOutRecordsSelector = (
    state
) =>
    state?.purchaseReport
        ?.paymentOutReport
        ?.records ||
    [];


export const purchasePaymentOutSummarySelector = (
    state
) =>
    state?.purchaseReport
        ?.paymentOutReport
        ?.summary ||
    DEFAULT_PAYMENT_OUT_SUMMARY;


export const purchasePaymentOutPaginationSelector = (
    state
) =>
    state?.purchaseReport
        ?.paymentOutReport
        ?.pagination ||
    DEFAULT_PAGINATION;


/*
|--------------------------------------------------------------------------
| OUTSTANDING SELECTORS
|--------------------------------------------------------------------------
*/

export const purchaseOutstandingReportSelector = (
    state
) =>
    state?.purchaseReport
        ?.outstandingReport ||
    initialState.outstandingReport;


export const purchaseOutstandingRecordsSelector = (
    state
) =>
    state?.purchaseReport
        ?.outstandingReport
        ?.records ||
    [];


export const purchaseOutstandingSummarySelector = (
    state
) =>
    state?.purchaseReport
        ?.outstandingReport
        ?.summary ||
    DEFAULT_OUTSTANDING_SUMMARY;


export const purchaseOutstandingPaginationSelector = (
    state
) =>
    state?.purchaseReport
        ?.outstandingReport
        ?.pagination ||
    DEFAULT_PAGINATION;


/*
|--------------------------------------------------------------------------
| AGEING SELECTORS
|--------------------------------------------------------------------------
*/

export const purchaseAgeingReportSelector = (
    state
) =>
    state?.purchaseReport
        ?.ageingReport ||
    initialState.ageingReport;


export const purchaseAgeingRecordsSelector = (
    state
) =>
    state?.purchaseReport
        ?.ageingReport
        ?.records ||
    [];


export const purchaseAgeingSummarySelector = (
    state
) =>
    state?.purchaseReport
        ?.ageingReport
        ?.summary ||
    DEFAULT_AGEING_SUMMARY;


export const purchaseAgeingPaginationSelector = (
    state
) =>
    state?.purchaseReport
        ?.ageingReport
        ?.pagination ||
    DEFAULT_PAGINATION;


/*
|--------------------------------------------------------------------------
| GST INPUT SELECTORS
|--------------------------------------------------------------------------
*/

export const purchaseGstInputReportSelector = (
    state
) =>
    state?.purchaseReport
        ?.gstInputReport ||
    initialState.gstInputReport;


export const purchaseGstInputRecordsSelector = (
    state
) =>
    state?.purchaseReport
        ?.gstInputReport
        ?.records ||
    [];


export const purchaseGstInputSummarySelector = (
    state
) =>
    state?.purchaseReport
        ?.gstInputReport
        ?.summary ||
    DEFAULT_GST_SUMMARY;


export const purchaseGstInputPaginationSelector = (
    state
) =>
    state?.purchaseReport
        ?.gstInputReport
        ?.pagination ||
    DEFAULT_PAGINATION;


/*
|--------------------------------------------------------------------------
| LOADING / ERROR
|--------------------------------------------------------------------------
*/

export const purchaseReportLoadingSelector = (
    state
) =>
    state?.purchaseReport
        ?.loading ||
    initialState.loading;


export const purchaseReportErrorSelector = (
    state
) =>
    state?.purchaseReport
        ?.error ||
    initialState.error;


/*
|--------------------------------------------------------------------------
| REDUCER
|--------------------------------------------------------------------------
*/

export default purchaseReportSlice.reducer;